import React from "react";
import {useTranslation} from "react-i18next";

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableFooter from '@mui/material/TableFooter';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Radio from "@mui/material/Radio";

import { RESULTS_COUNT, SORTING_OPTIONS} from "../../../../../../../../utils/constants";

import EnhancedTableHead, {HeaderCell} from './components/EnhancedTableHead';
import TablePaginationActions from "./components/TablePaginationActions";

type Preferences = {
    id: string
    width: number
}

type Props = {
    rows: any[],
    headCells: HeaderCell[]
    preferences: Preferences[]
    count?: number
    primaryField?: string
    paddingIsEqual?: boolean
    checkboxesWidth: string
    handleSendRequest?(data: DictionaryParamsInput): void
    chooseItem?(id: string): void
}

const SettingsTable = ({
  rows,
  headCells,
  preferences,
  handleSendRequest,
  count,
  primaryField,
  chooseItem,
  checkboxesWidth
}: Props) => {

  const { t } = useTranslation()

  const [ page, setPage ] = React.useState(0);
  const [ rowsPerPage, setRowsPerPage ] = React.useState(RESULTS_COUNT.COUNT_10);
  const [ orderBy, setOrderBy ] = React.useState<string>(SORTING_OPTIONS.NAME_DESC);
  const [ selected, setSelected ] = React.useState('');

  if (!count || !primaryField) {
    return null;
  }

  const sendRequest = (data: DictionaryParamsInput) => {
    handleSendRequest && handleSendRequest({
      page: page + 1,
      limit: rowsPerPage,
      ordering: orderBy,
      ...data
    })
  }

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
    sendRequest({
      page: newPage + 1
    });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const limit = parseInt(event.target.value, 10);
    setRowsPerPage(limit);
    setPage(0);
    sendRequest({
      limit,
      page: 1,
    });
  };

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: string,
  ) => {
    setOrderBy(property);
    sendRequest({
      ordering: property
    })
  };


  const handleClick = (event: React.MouseEvent<unknown>, id: string) => {
    setSelected(id);
    chooseItem && chooseItem(id);
  };

  const isSelected = (id: string) => selected === id;

  return (
    <>
      <TableContainer component={Paper}>
        <Table sx={{minWidth: 1072}}>
          <EnhancedTableHead
            numSelected={selected.length}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            rowCount={rowsPerPage}
            count={count}
            headCells={headCells}
            preferences={preferences}
            checkboxesWidth={checkboxesWidth}
            paddingIsEqual
          />
          <TableBody>
            {rows.map((row, index) => {
              const isItemSelected = isSelected(row[primaryField]);
              return (
                <TableRow
                  hover
                  onClick={(event) => handleClick(event, row[primaryField])}
                  role='checkbox'
                  aria-checked={isItemSelected}
                  tabIndex={-1}
                  key={row[primaryField]}
                  selected={isItemSelected}
                  sx={{
                    cursor: 'pointer'
                  }}
                >
                  <TableCell padding='checkbox' sx={{width: '26px', padding: 0}}>
                    <Radio
                      checked={isItemSelected}
                      // @ts-ignore
                      onChange={handleClick}
                      value={row.id}
                    />
                  </TableCell>
                  {preferences.map((preference, index) => (
                    <TableCell
                      component='th'
                      scope='row'
                      style={{width: preference.width}}
                      key={preference.id}
                      sx={{padding: '16px 16px 13px 0'}}
                    >
                      {row[preference.id]}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[ RESULTS_COUNT.COUNT_10, RESULTS_COUNT.COUNT_20, RESULTS_COUNT.COUNT_50 ]}
                colSpan={10}
                count={count}
                rowsPerPage={rowsPerPage}
                page={page}
                labelRowsPerPage={t('common__text__rowsCount')}
                labelDisplayedRows={({from, to, count}) => {
                  return `${from}–${to} ${t('common__text__of')} ${count !== -1 ? count : `${t('common__text__moreThan')} ${to}`}`;
                }}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                ActionsComponent={TablePaginationActions}
                sx={{
                  ':last-child': {
                    paddingRight: 1,
                  },
                  '.MuiTablePagination-displayedRows': {
                    fontSize: '12px',
                  }
                }}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </>
  )
};

export default SettingsTable
